import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Pencil } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';
import { AuditTrailViewer } from '../../components/AuditTrail';
import TabNavigation from '../../components/TabNavigation';

const CompetencyMatrixDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const competencyDescriptions = {
    1: 'Basic/Novice',
    2: 'Limited/Advanced Beginner',
    3: 'Competent/Intermediate',
    4: 'Proficient/Advanced',
    5: 'Expert/Master'
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [matrixRes, coursesRes] = await Promise.all([
        axios.get(`${API}/competency-matrix/${id}`, { headers }),
        axios.get(`${API}/training-courses`, { headers })
      ]);

      setMatrix(matrixRes.data);
      setCourses(coursesRes.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load competency matrix');
      setLoading(false);
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.course_title : courseId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !matrix) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Competency matrix not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/training/competency')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Competency Matrix
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/training/competency')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Competency Matrix
            </Button>

            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{matrix.role_name}</h1>
                <p className="text-gray-600 mt-1">
                  Competency Level: {matrix.competency_level || 3}/5 — {competencyDescriptions[matrix.competency_level || 3]}
                </p>
              </div>
              {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/training/competency?edit=${id}`)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'audit'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Audit History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Role Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Role Name</label>
                      <p className="text-gray-900 mt-1 text-lg font-semibold">{matrix.role_name}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Competency Level</label>
                      <p className="text-gray-900 mt-1 text-lg font-semibold">
                        {matrix.competency_level || 3}/5
                      </p>
                      <p className="text-sm text-gray-500">{competencyDescriptions[matrix.competency_level || 3]}</p>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Created</label>
                      <p className="text-gray-900 mt-1">{formatDateTime(matrix.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Required Courses */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Required Training Courses ({matrix.required_courses?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {matrix.required_courses && matrix.required_courses.length > 0 ? (
                    <ul className="space-y-2">
                      {matrix.required_courses.map(courseId => (
                        <li key={courseId} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                          <span className="text-gray-900">{getCourseName(courseId)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">No required courses defined</p>
                  )}
                </CardContent>
              </Card>

              {/* Required Certifications */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Required Certifications ({matrix.required_certifications?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {matrix.required_certifications && matrix.required_certifications.length > 0 ? (
                    <ul className="space-y-2">
                      {matrix.required_certifications.map(certType => (
                        <li key={certType} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                          <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
                          <span className="text-gray-900">{certType}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">No required certifications defined</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <AuditTrailViewer
                  entityType="competency_matrix"
                  entityId={id}
                  title="Competency Matrix Audit History"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetencyMatrixDetail;
